import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { ConfirmSheet } from "../components/ConfirmSheet";
import { HowWasAsk } from "../components/HowWasIt";
import { PlayerShell, usePlayerOrigin } from "../components/PlayerShell";
import { SegmentBar } from "../components/SegmentBar";
import { Tag } from "../components/Tag";
import { I } from "../components/icons";
import { DEFAULT_STEP_GOAL } from "../domain/activity";
import { hasCounterApp } from "../domain/counterApp";
import { dayLabel, fmtClock, todayStr } from "../domain/date";
import { LIVE_BASELINE_TOLERANCE_MS, daySteps, measuredSince, strideMeters } from "../domain/energy";
import { quote } from "../domain/progress";
import type { TreadmillPhase } from "../domain/types";
import { cadenceLine, walkCadence, walkTotals } from "../domain/walk";
import {
  BRISK_WORDS,
  EASY_WORDS,
  INTERVAL_ROUNDS,
  briskRoundsDone,
  cueSchedule,
  cueText,
  dueCue,
  intervalPhaseName,
  intervalPhases,
  intervalPosition,
  intervalRound,
  intervalSecsLeft,
  nextCue,
  type CueId,
} from "../domain/walkIntervals";
import { pingSession, sessionExtra } from "../lib/appBridge";
import { beep, buzz, cancelNotification, isAndroid, scheduleNotification, setWakeLock } from "../lib/device";
import { useDocumentVisible } from "../lib/useDocumentVisible";
import { voice } from "../lib/voice";
import { addWalk } from "../store/toggles";
import { useKaya, usePlayerMode } from "../store/kayaStore";
import { api } from "../sync/api";

/** How often to ask the server what the phone has counted. The counter app posts on the same
 *  cadence while a session is open, so the number on screen trails reality by a few seconds. */
const POLL_MS = 10_000;

/**
 * A free walk.
 *
 * A browser still cannot read the pedometer — that has not changed and cannot be worked around.
 * What it can do is ask: while a session is open the counter app posts its reading every ten
 * seconds, and this page reads those back, so the step count moves during the walk instead of
 * only appearing afterwards. Without a connection it falls back to the original behaviour, and
 * the exact figure fills in on the next sync; the walk is never lost either way.
 *
 * The walk itself lives in the store's session record rather than here or in the synced blob:
 * the blob's copy was erased by the first sync, so leaving for the counter app and coming back
 * started a second walk on top of the first. (ADR-027)
 *
 * `/walk?mode=intervals` runs the interval walk: the rounds are phases with no speed, placed from
 * the walked time, so a pause stops both clocks. Either walk speaks its cues from here only when the
 * counter app is not on the phone — when it is, the wrapper speaks them, screen off or not, and
 * this page only beeps at a boundary. (ADR-063)
 */
const NOTIFY_ID = "walk";

/** What the line says the next cue is: "Half-way at 15:00". */
const NEXT_CUE_WORDS: Record<CueId, string> = {
  minutes: "Next cue at",
  "turn-back": "Half-way at",
  "planned-done": "Planned walk ends at",
  brisk: "Brisk at",
  easy: "Easy at",
  "rounds-done": "Rounds done at",
};

export default function WalkRun() {
  const history = useHistory();
  const { S, update, checkBadges, user, session, beginSession, patchSession, endSession, meta } = useKaya();
  const player = usePlayerMode();
  const visible = useDocumentVisible();
  const today = todayStr();
  /** Minimise, Close, End and the sub-minute discard go back to where the walk was started. */
  const origin = usePlayerOrigin("/train/walks");

  // Resume the walk in progress if there is one: leaving for the counter app, a relaunch, or the
  // browser reclaiming the tab must not lose a walk already under way.
  const existing = session?.kind === "walk" ? session : null;
  // One session at a time. Reached with another kind open — a typed URL, a stale link — this
  // page hands over to it rather than starting a walk on top.
  const other = session && session.kind !== "walk" ? session : null;
  /** A resumed walk re-announces itself to the counter app on the first tap: Chrome drops an
   *  `intent:` launch that no gesture asked for, so a mount cannot do it. */
  const announced = useRef(!existing);
  // A resumed walk carries its own mode, whatever the URL: the wrapper's notification opens plain
  // `/walk` for an interval walk too.
  const [mode] = useState<"intervals" | undefined>(() =>
    existing
      ? existing.mode
      : new URLSearchParams(history.location.search).get("mode") === "intervals"
        ? "intervals"
        : undefined
  );
  const intervals = mode === "intervals";
  const [plannedMin] = useState<number | null>(() =>
    existing ? (existing.plannedMin ?? null) : intervals ? null : (meta.walkPlanMin ?? null)
  );
  const [phases] = useState<TreadmillPhase[]>(() =>
    intervals ? (existing?.phases?.length ? existing.phases : intervalPhases()) : []
  );
  const schedule = useMemo(() => cueSchedule(plannedMin, intervals ? "intervals" : "free"), [plannedMin, intervals]);
  /** the page speaks only where the wrapper cannot (C4) */
  const speaker = !hasCounterApp(S, today, isAndroid());
  const [startedAt] = useState(() => existing?.startedAt || new Date().toISOString());
  const [running, setRunning] = useState(() => (existing ? existing.since !== null : true));
  /** milliseconds banked from finished running stretches */
  const [banked, setBanked] = useState(() => existing?.banked ?? 0);
  const [since, setSince] = useState(() => existing?.since ?? Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [finished, setFinished] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  /** null = nothing measured yet, which reads differently from a measured zero */
  const [liveSteps, setLiveSteps] = useState<number | null>(null);
  const [doneRounds, setDoneRounds] = useState<number | null>(null);

  /** The day's steps as last reported before this walk began — only for a fresh walk; a resumed
   *  walk's day figure may already hold part of it, and adding the live count would count twice. */
  const [dayBefore] = useState(() => (existing ? 0 : daySteps(S, today)));

  const startMs = Date.parse(startedAt);
  /** Read at call time: ending the session clears it, and a stop must name it first. */
  const sid = () => useKaya.getState().session?.sid;
  const extra = () => sessionExtra({ kind: "walk", mode, plannedMin }, strideMeters(S), player);
  /** Never while paused: a resume moves the wrapper's clock on (C5). The Resume tap sends its own. */
  const announce = () => {
    if (announced.current) return;
    announced.current = true;
    if (running) pingSession("resume", Date.now(), sid(), extra());
  };

  useEffect(() => {
    if (other) history.replace(other.route);
  }, [other, history]);

  // Wall-clock, never accumulated ticks: a backgrounded tab has its interval throttled and a
  // tick counter would lose minutes over a long walk.
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    // Only a Start tap's push begins a walk; a reload or a deep link with no record goes back to
    // where a walk is started from (ADR-035, the treadmill's restart).
    if (!existing && !other && history.action !== "PUSH") {
      history.replace(origin);
      return;
    }
    void setWakeLock(true);
    if (!existing && !other) {
      const s = beginSession({
        kind: "walk",
        route: intervals ? "/walk?mode=intervals" : "/walk",
        startedAt,
        banked: 0,
        since,
        ...(intervals
          ? { mode: "intervals" as const, phases, idx: 0, phaseEndsAt: since + phases[0].secs * 1000 }
          : { plannedMin }),
        cueAt: 0,
      });
      // Ask the counter app for a reading right now, stamped with this walk's own start, so the
      // window has an exact edge rather than one a second or two inside it.
      pingSession("start", startMs, s.sid, extra());
      if (intervals) notifyNext(0, phases[0].secs);
    }
    return () => {
      void setWakeLock(false);
      // a minimised walk has the LiveBar and the wrapper; a queued phase notice would outlive it
      cancelNotification(NOTIFY_ID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for what the phone has counted since this walk began. Stops the moment the walk is
  // finished, and stays quiet while offline rather than retrying into a dead network.
  useEffect(() => {
    if (finished || !user) return;
    let cancelled = false;

    const poll = async () => {
      if (!navigator.onLine) return;
      try {
        const { samples } = await api.pullSamples(startMs - LIVE_BASELINE_TOLERANCE_MS, Date.now());
        if (cancelled) return;
        const n = measuredSince(samples, startMs, Date.now());
        if (n !== null) setLiveSteps(n);
      } catch {
        /* a missed poll is a stale number for ten seconds, not a broken walk */
      }
    };

    void poll();
    const iv = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [finished, user, startMs]);

  const elapsedMs = banked + (running ? now - since : 0);
  const secs = Math.round(elapsedMs / 1000);
  const activeSec = Math.floor(elapsedMs / 1000);
  const pos = intervals ? intervalPosition(phases, elapsedMs / 1000) : null;

  /** The phase notice for when the page is hidden: only where the page is the one speaking. */
  function notifyNext(idx: number, leftSec: number) {
    cancelNotification(NOTIFY_ID);
    if (!speaker || !phases[idx + 1]) return;
    const nx = phases[idx + 1];
    scheduleNotification(
      NOTIFY_ID,
      Date.now() + leftSec * 1000,
      "Interval walk: " + intervalPhaseName(phases, idx + 1),
      nx.c === "brisk" ? BRISK_WORDS : EASY_WORDS
    );
  }

  /** The record's phase fields, as the treadmill keeps them, so a relaunch reads the same rounds. */
  function recordPhase(idx: number, leftSec: number, runningNow: boolean) {
    patchSession((s) => {
      s.idx = idx;
      s.phaseEndsAt = runningNow ? Date.now() + leftSec * 1000 : null;
      s.phaseLeftMs = runningNow ? undefined : leftSec * 1000;
    });
  }

  // Boundaries and cues, once a walked second. The first look after a mount only takes a bearing:
  // an edge passed while nothing was watching is history, not a cue.
  const edge = useRef<{ idx: number; over: boolean } | null>(null);
  const cueAt = useRef<number>(existing ? (existing.cueAt ?? activeSec) : 0);
  useEffect(() => {
    if (finished) return;
    if (pos) {
      const prev = edge.current;
      edge.current = { idx: pos.idx, over: pos.over };
      if (prev && pos.over && !prev.over) {
        beep(1318, 400, 0.2, S.profile.sound);
        buzz([200, 100, 200, 100, 200], S.profile.vibrate);
        cancelNotification(NOTIFY_ID);
      } else if (prev && !pos.over && pos.idx !== prev.idx) {
        beep(phases[pos.idx].c === "brisk" ? 1046 : 660, 300, 0.2, S.profile.sound);
        buzz([200, 100, 200], S.profile.vibrate);
        recordPhase(pos.idx, pos.leftSec, running);
        if (running) notifyNext(pos.idx, pos.leftSec);
      }
    }
    const due = dueCue(schedule, cueAt.current, activeSec);
    if (!due) return;
    // Advanced whether or not it is spoken, so coming back to the screen never speaks a stale cue.
    cueAt.current = due.atSec;
    patchSession((s) => {
      s.cueAt = due.atSec;
    });
    if (!speaker || !player.voice || !running || !visible) return;
    if (due.id === "minutes") void voice.sayText(cueText("minutes", player.lang, due.atSec / 60, liveSteps), player.lang);
    else void voice.say([due.id], player.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSec, finished]);
  const live = walkTotals(S, secs, liveSteps);
  const liveKm = liveSteps !== null ? (liveSteps * strideMeters(S)) / 1000 : null;

  const toggle = () => {
    announce();
    if (running) {
      const b = banked + (Date.now() - since);
      setBanked(b);
      setRunning(false);
      patchSession((s) => {
        s.banked = b;
        s.since = null;
      });
      pingSession("pause", Date.now(), sid());
      if (intervals) {
        const p = intervalPosition(phases, b / 1000);
        cancelNotification(NOTIFY_ID);
        if (!p.over) recordPhase(p.idx, p.leftSec, false);
      }
    } else {
      const now2 = Date.now();
      setSince(now2);
      setRunning(true);
      patchSession((s) => {
        s.since = now2;
      });
      pingSession("resume", now2, sid(), extra());
      if (intervals) {
        const p = intervalPosition(phases, banked / 1000);
        if (!p.over) {
          recordPhase(p.idx, p.leftSec, true);
          notifyNext(p.idx, p.leftSec);
        }
      }
    }
  };

  const discard = () => {
    setAskDiscard(false);
    cancelNotification(NOTIFY_ID);
    // A discarded walk is still an ended one: the counter app was told it started, and left
    // untold it would hold the session — and its notification — until something else stopped it.
    pingSession("stop", Date.now(), sid());
    endSession();
    // `replace`, on every way out of a player: Ionic keeps a pushed page mounted and hands it
    // back for the next push to the same path, which would be this walk's screen — clock,
    // record and all — presented as the next one. A replace unmounts it.
    history.replace(origin);
  };

  const finish = () => {
    // Bank the running stretch before stopping the clock.
    //
    // `elapsedMs` is `banked` plus the stretch since the last resume, and `banked` only ever grew
    // in `toggle`. Stopping without banking meant that on a walk nobody paused — the normal case —
    // the summary re-rendered with `banked` still at zero and reported "0:00 walked" and 0 kcal
    // over a real step count. The logged walk was right all along; only the screen confirming it
    // was wrong, which is the worse way round to get it.
    //
    // Read from the clock rather than the `now` state, which is half a second stale by design.
    const totalMs = banked + (running ? Date.now() - since : 0);
    const walked = Math.round(totalMs / 1000);

    if (walked < 60) {
      setAskDiscard(true);
      return;
    }
    setBanked(totalMs);
    setRunning(false);
    setFinished(true);
    cancelNotification(NOTIFY_ID);
    beep(1318, 400, 0.2, S.profile.sound);
    buzz([200, 100, 200], S.profile.vibrate);
    void setWakeLock(false);

    const at = new Date().toISOString();
    // Deliberately no `measuredSteps` here, even though a live figure is usually on screen: the
    // closing reading is taken by the ping below and lands a moment later, and the sync backfill
    // only fills walks that have none. Writing the near-final number would lock out the exact one.
    const t = walkTotals(S, walked, liveSteps);
    const p = intervals ? intervalPosition(phases, totalMs / 1000) : null;
    const rounds = p ? briskRoundsDone(phases, p.over ? phases.length : p.idx) : null;
    setDoneRounds(rounds);
    update((d) => {
      addWalk(
        d,
        today,
        { startedAt, at, secs: walked, kcal: t.kcal, ...(rounds !== null ? { mode: "intervals" as const, rounds } : {}) },
        Date.now()
      );
      d.activeWalk = null;
    });
    // Closes the window with an exact reading, and flushes anything buffered.
    pingSession("stop", Date.now(), sid());
    endSession();
    checkBadges();
  };

  /** End, from the More menu. Its inline confirm is the question the old `window.confirm` asked
   *  (which it skipped under a minute, when there was nothing to lose). */
  const abandon = () => {
    void setWakeLock(false);
    cancelNotification(NOTIFY_ID);
    pingSession("stop", Date.now(), sid());
    endSession();
    history.replace(origin);
  };

  /** Leaves the walk going; the LiveBar takes over. No ping — nothing ended. */
  const minimise = () => history.replace(origin);

  const soundItem = {
    label: "Sound",
    checked: S.profile.sound,
    onClick: () =>
      update((d) => {
        d.profile.sound = !d.profile.sound;
      }),
  };

  const stepsStat = {
    figure: liveSteps,
    label: liveSteps !== null ? "steps" : finished ? "steps pending" : "steps after sync",
    tag: liveSteps !== null ? <Tag tone="phone">{finished ? "phone" : "phone · live"}</Tag> : undefined,
  };

  const cadence = walkCadence({ measuredSteps: liveSteps ?? undefined, secs });
  const doneCadence = cadenceLine({ measuredSteps: liveSteps ?? undefined, secs });

  if (finished) {
    return (
      <PlayerShell
        surface="done"
        title={`${intervals ? "Interval walk" : "Walk"} · ${dayLabel(today)}`}
        // asked after a walk only for someone with a training plan, for whom a walk is a session
        children={S.profile.training ? <HowWasAsk ds={today} sub="Walk · one tap" talk /> : null}
        onMinimise={() => history.replace(origin)}
        finished={{
          heading: intervals ? "Interval walk logged" : "Walk logged",
          sub: doneRounds !== null ? `${doneRounds} of ${INTERVAL_ROUNDS} brisk rounds` : undefined,
          stats: [
            { figure: fmtClock(secs * 1000), label: "walked" },
            { figure: live.kcal, label: "kcal", estimated: true },
            stepsStat,
          ],
          line:
            liveSteps !== null
              ? `${doneCadence ? `${doneCadence}. ` : ""}Confirmed against your phone's own count on the next sync.`
              : "Steps and distance fill in from your phone on the next sync.",
          quote: quote(),
          onDone: () => history.replace("/today"),
          // A distance you vouch for is what teaches the stride (ADR-027 §14); it is added on
          // the walks page.
          secondary: {
            label: "Add the distance walked",
            icon: I.edit,
            onClick: () => history.replace("/train/walks"),
          },
        }}
      />
    );
  }

  const goal = S.profile.stepGoal || DEFAULT_STEP_GOAL;
  // Both terms are lower bounds on today's steps, so the larger never counts a step twice.
  const todaySteps = liveSteps !== null ? Math.max(daySteps(S, today), dayBefore + liveSteps) : null;

  const coming = player.voice ? nextCue(schedule, activeSec) : null;
  const cueLine = coming ? `${NEXT_CUE_WORDS[coming.id]} ${fmtClock(coming.atSec * 1000)}` : null;

  const freeRing = {
    pct: todaySteps !== null ? todaySteps / goal : 0,
    figure: fmtClock(elapsedMs),
    unit: running ? "elapsed" : "paused",
    label:
      `${fmtClock(elapsedMs)} walked` +
      (todaySteps !== null ? `; ${todaySteps.toLocaleString()} of ${goal.toLocaleString()} steps today` : ""),
  };
  const freeName =
    todaySteps !== null
      ? `${todaySteps.toLocaleString()} of ${goal.toLocaleString()} steps today`
      : running
        ? "Walking"
        : "Paused";
  const freeCue =
    todaySteps !== null
      ? todaySteps >= goal
        ? "Today's step goal is reached"
        : `${(goal - todaySteps).toLocaleString()} to go · the ring fills as the phone reports`
      : "Keep your phone on you. The count appears once the Kaya Steps app reports in — a " +
        "browser cannot read the pedometer itself, so it asks.";

  // The interval walk's own figures while rounds remain; after them it is a free walk home.
  const rounds = pos && !pos.over ? pos : null;
  const cur = rounds ? phases[rounds.idx] : null;
  const curName = rounds ? intervalPhaseName(phases, rounds.idx) : "";
  const surface = !running || !cur ? "ink" : cur.c === "brisk" ? "work" : cur.c === "easy" ? "rest" : "ink";
  const roundWords = rounds
    ? rounds.idx === 0
      ? "Warm-up"
      : `Round ${intervalRound(rounds.idx)} of ${INTERVAL_ROUNDS}`
    : "";

  return (
    <>
      <PlayerShell
        surface={surface}
        title={intervals ? "Interval walk" : "Walk"}
        onMinimise={minimise}
        elapsed={intervals ? fmtClock(elapsedMs) : undefined}
        menu={[soundItem]}
        end={{
          label: "End walk",
          question: "End the walk without logging it?",
          onConfirm: abandon,
        }}
        segments={
          rounds ? (
            <SegmentBar
              count={phases.length}
              current={rounds.idx}
              progress={1 - rounds.leftSec / phases[rounds.idx].secs}
              weights={phases.map((ph) => ph.secs)}
              onSurface
            />
          ) : undefined
        }
        line={
          rounds
            ? `${running ? "" : "Paused · "}${roundWords} · ${fmtClock(intervalSecsLeft(phases, elapsedMs / 1000) * 1000)} to go`
            : !running
              ? "Paused"
              : intervals
                ? null
                : cueLine
        }
        ring={
          rounds
            ? {
                pct: 1 - rounds.leftSec / phases[rounds.idx].secs,
                figure: fmtClock(rounds.leftSec * 1000),
                unit: running ? `left in ${curName.toLowerCase()}` : "paused",
                label: `${fmtClock(rounds.leftSec * 1000)} left in ${curName.toLowerCase()}`,
              }
            : freeRing
        }
        name={intervals ? (rounds ? curName : "Rounds done") : freeName}
        cue={
          intervals
            ? rounds
              ? cur?.c === "brisk"
                ? BRISK_WORDS
                : cur?.c === "easy"
                  ? EASY_WORDS
                  : "An easy three minutes first"
              : "Walk home easy; finish when you stop."
            : freeCue
        }
        next={
          rounds
            ? phases[rounds.idx + 1]
              ? `Next · ${intervalPhaseName(phases, rounds.idx + 1)}`
              : "Next · rounds done"
            : null
        }
        stats={[
          { figure: live.kcal, label: "kcal", estimated: true },
          stepsStat,
          {
            figure: liveKm !== null ? liveKm.toFixed(2) : null,
            label: liveKm !== null ? "km" : "km after sync",
            estimated: true,
          },
          { figure: cadence, label: cadence !== null ? "steps/min" : "steps/min after 2 min" },
        ]}
        note={liveSteps !== null ? "Counting from your phone, refreshed every few seconds. Keep it on you." : null}
        centre={{
          label: running ? "Pause" : "Resume",
          icon: running ? I.pause : I.play,
          onClick: toggle,
        }}
        right={{ label: "Finish walk", icon: I.tick, onClick: finish }}
      />
      {askDiscard ? (
        <ConfirmSheet
          title="Less than a minute — discard this walk?"
          sub="Nothing is logged. Keep walking to log it."
          confirm={{ label: "Discard walk", onClick: discard }}
          cancel="Keep walking"
          onClose={() => setAskDiscard(false)}
        />
      ) : null}
    </>
  );
}
